/**
 * Página de documentos e liberação do paciente ecommerce (rota Gerenciar).
 * @module ManageDocumentsRelease
 */

import { memo } from "react";
import { Box, Stack } from "@mui/material";
import type { Patient } from "fhir/r5";
import { CommonDialog } from "@/components/common/CommonDialog";
import { PreviousReleasesSection } from "src-next/modules/ecommerce";
import DocumentInlinePreview from "../components/DocumentInlinePreview";
import { ConfirmReleaseDialog } from "./ConfirmReleaseDialog";
import { ManageDocumentsBody } from "./ManageDocumentsBody";
import { ManageDocumentsFooter } from "./ManageDocumentsFooter";
import { ManageDocumentsHeader } from "./ManageDocumentsHeader";
import { useManageDocumentsRelease } from "./useManageDocumentsRelease";

interface ManageDocumentsReleasePageProps {
  patient: Patient | null;
  organizationId?: string;
  transactionId?: string;
  onClose: () => void;
  onSendWhatsApp?: (
    patients: Patient[],
    options?: { onSent?: () => void },
  ) => void;
}

const ManageDocumentsReleasePage = memo(
  function ManageDocumentsReleasePage({
    patient,
    organizationId,
    transactionId,
    onClose,
    onSendWhatsApp,
  }: ManageDocumentsReleasePageProps) {
    const page = useManageDocumentsRelease({
      patient,
      organizationId,
      transactionId,
      onClose,
    });

    if (!patient) return null;

    return (
      <>
        <Box
          sx={{
            width: "100%",
            minHeight: { xs: 520, md: 640 },
            display: "flex",
            flexDirection: "column",
            border: 1,
            borderColor: "grey.200",
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "background.paper",
            boxSizing: "border-box",
            p: { xs: 1.5, sm: 2 },
            gap: 1.5,
          }}
        >
          <Stack
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              maxHeight: "100%",
              overflow: "hidden",
              gap: 1.5,
            }}
          >
            <ManageDocumentsHeader
              name={page.name}
              previousCount={page.releases.listItems.length}
              submitting={page.submitting}
              whatsAppLabel={
                page.transactionOpState === "docs_pending"
                  ? "Lembrar documentos"
                  : page.transactionOpState === "docs_rejected"
                    ? "Avisar recusa"
                    : page.transactionOpState === "docs_released" ||
                        page.transactionOpState === "purchase_completed"
                      ? "Convidar para compra"
                      : undefined
              }
              onWhatsApp={
                onSendWhatsApp && patient
                  ? () => onSendWhatsApp([patient])
                  : undefined
              }
              onClose={onClose}
              onOpenPrevious={() => page.setPreviousOpen(true)}
            />

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <ManageDocumentsBody
                step={page.step}
                onStepChange={page.setStep}
                docsLoading={page.docs.loading}
                docsRefreshing={page.docs.refreshing}
                categories={page.docs.categories}
                organizationId={organizationId}
                practitionerRoleId={page.practitionerRoleId}
                transactionId={page.transactionId}
                patient={patient}
                interactionDisabled={page.docsInteractionLocked}
                docsError={page.docs.error}
                forbidden={page.docs.forbidden}
                missingCpf={page.docs.missingCpf}
                onRefetch={page.docs.refetch}
                onSendWhatsApp={onSendWhatsApp}
                onView={page.openDoc}
                canRelease={page.canRelease}
                hasRejected={page.hasRejected}
                storeError={page.storeError}
                storeReady={page.storeReady}
                products={page.products}
                productsLoading={page.productsLoading}
                productsError={page.productsError}
                lines={page.lines}
                onAddProduct={page.handleAddProduct}
                onUpdateQuantity={page.handleUpdateQuantity}
              />
            </Box>

            <ManageDocumentsFooter
              step={page.step}
              summaryText={page.summaryText}
              canRelease={page.canRelease}
              hasRejected={page.hasRejected}
              docsOk={page.docsOk}
              canConfirm={page.canConfirm}
              submitting={page.submitting}
              docsRefreshing={page.docs.refreshing}
              onAdvance={() => page.setStep(1)}
              onConfirm={() => page.setConfirmOpen(true)}
            />
          </Stack>
        </Box>

        {page.confirmOpen ? (
          <ConfirmReleaseDialog
            name={page.name}
            lines={page.lines}
            validUntil={page.validUntil}
            submitting={page.submitting}
            today={page.today}
            onValidUntilChange={page.handleValidUntilChange}
            onClose={() => page.setConfirmOpen(false)}
            onConfirm={() => void page.handleConfirm()}
          />
        ) : null}

        {page.previousOpen ? (
          <CommonDialog
            isOpen
            handleClose={() => page.setPreviousOpen(false)}
            maxWidth="sm"
            sheetTitle="Liberações anteriores"
            sheetSubtitle={page.name}
          >
            <Box
              sx={{
                px: { xs: 0, md: 1 },
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              <PreviousReleasesSection
                items={page.releases.listItems}
                loading={page.releases.loading}
                refreshing={page.releases.refreshing}
                onRevoke={page.releases.revokeBatch}
              />
            </Box>
          </CommonDialog>
        ) : null}

        {page.preview ? (
          <CommonDialog
            isOpen
            handleClose={() => page.setPreview(null)}
            maxWidth="md"
            sheetTitle={page.preview.title}
          >
            <Box sx={{ px: { xs: 0, md: 1 } }}>
              <DocumentInlinePreview
                title={page.preview.title}
                urlBinary={page.preview.url}
                onClose={() => page.setPreview(null)}
              />
            </Box>
          </CommonDialog>
        ) : null}
      </>
    );
  },
);

export default ManageDocumentsReleasePage;
